<script setup lang="ts">

import {onUnmounted, PropType, provide, ref, watch} from "vue";
import {
  injectKeyFormArrayContainer, injectKeyFormConfig,
  injectKeyFormData, injectKeyFormFieldLayout,
  injectKeyUpdateFormData,
  injectRegisterFormPreExcuteHooks, injectRegisterFormResetHooks,
  injectRegisterFormValidateHooks,
  injectUnRegisterFormPreExcuteHooks,
  injectUnRegisterFormValidateHooks
} from "/@src/constants/keys";
import {setNestedField} from "/@src/utils";
import {FormFieldLayout} from "/@src/components/custom/form/BaseFormField.vue";

export interface FormConfig {
  layout?: FormFieldLayout
  labelHighlight?: boolean
  loading?: boolean
  inPanel?: boolean
  ignoreUpdate?: boolean
}

export interface FormGeneralOption {
  disabled?:boolean
  rules?:object
}

export interface FormInputOption extends FormGeneralOption{
  limit: number
  placeholder: string
}

export type FormValidatorTrigger = 'blur'|'change'|'submit'|'init'

export interface FormValidatorRule {
  validator?:(field:string,value:any) => boolean
  message:string
  required?:boolean
  limit?:number
  trigger?:FormValidatorTrigger[]
  callback?:(isSuccess:boolean)=>void
}

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => {},
  },
  layout: {
    type: String as PropType<FormFieldLayout>,
    default: 'field',
  },
  labelHighlight: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  inPanel:{
    type: Boolean,
    default: false,
  },
  /**
   * 是否忽略表单更新
   */
  ignoreUpdate:{
    type: Boolean,
    default: false,
  }
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
}>()

const formData = ref(props.modelValue||{})
const formConfig = ref<FormConfig>({})
const formPreExcuteHooks:Array<() => Promise<any>> = []
const formValidateHooks:Array<() => Promise<boolean>> = []
const formResetHooks:Array<() => Promise<any>> = []
const refContainer = ref<HTMLDivElement>()


const setupConfig = () => {
  formConfig.value = {
    layout: props.layout,
    labelHighlight: props.labelHighlight,
    loading: props.loading,
    inPanel: props.inPanel,
    ignoreUpdate: props.ignoreUpdate
  }
}

setupConfig()


watch(()=>props.ignoreUpdate,(newData) => {
  formConfig.value.ignoreUpdate = newData
})

watch(()=>props.modelValue,(newData) => {
  formData.value = newData
})

watch(()=>props.loading,(newData)=>{
  formConfig.value.loading = newData
})

const updateModelValue = (field:string|null, value:any, index?:number) => {
  if (!formData.value) return
  if (index !== undefined && index >= 0){
    if ((formData.value as Array<any>).length <= index) return
    if (field) {
      if (field.includes(".")){
        setNestedField(formData.value[index], field, value)
      } else {
        formData.value[index][field] = value
      }
    }else {
      formData.value[index] = value
    }
  } else {
    if (field) {
      if (field.includes(".")){
        setNestedField(formData.value, field, value)
      } else {
        formData.value[field] = value
      }
    }else {
      // form必须要包含字段
    }
  }
  emit('update:modelValue', formData.value)
};

/**
 * 校验表单
 */
const submit = async <Model>():Promise<Model> => {
  const results = await Promise.allSettled(formValidateHooks.map(async (hook) => {
    return await hook();
  }))
  if (!results.every(result => result.status === "fulfilled" && result.value)) {
    console.log('valid failed')
    return Promise.reject(Error('valid failed'))
  }
  await executePreHooks()
  return formData.value as Model
}

const reset = () => {
  return Promise.all(formResetHooks.map(async (hook) => {
    return await hook();
  }))
}

/**
 * 执行前置钩子
 */
const executePreHooks = async () => {
  console.log('executing pre hooks')
  await Promise.all(formPreExcuteHooks.map(async (hook) => {
    return await hook();
  }))
}

const registerFormPreExecuteHook = (hook:()=>Promise<any>) => {
  formPreExcuteHooks.push(hook)
}

const registerFormResetHook = (hook:()=>Promise<any>) => {
  formResetHooks.push(hook)
}

const unrRegisterFormPreExecuteHook = (hook:()=>Promise<any>) => {
  const index = formPreExcuteHooks.indexOf(hook)
  if (index >= 0){
    formPreExcuteHooks.splice(index,1)
  }
}

const registerFormValidateHook = (hook:()=>Promise<boolean>) => {
  formValidateHooks.push(hook)
}

const unRegisterFormValidateHook = (hook:()=>Promise<boolean>) => {
  const index = formValidateHooks.indexOf(hook)
  if (index >= 0){
    formValidateHooks.splice(index,1)
  }
}

provide(injectKeyFormData, formData);
provide(injectKeyFormConfig, formConfig);
provide(injectKeyFormFieldLayout, props.layout);
provide(injectKeyUpdateFormData, updateModelValue);
provide(injectKeyFormArrayContainer, null);
// 注册前置执行钩子
provide(injectRegisterFormPreExcuteHooks, registerFormPreExecuteHook);
provide(injectUnRegisterFormPreExcuteHooks, unrRegisterFormPreExecuteHook);
// 注册校验，当提交表单时触发
provide(injectRegisterFormValidateHooks, registerFormValidateHook);
provide(injectUnRegisterFormValidateHooks, unRegisterFormValidateHook);
// 注册重置
provide(injectRegisterFormResetHooks, registerFormResetHook);

onUnmounted(()=>{
  formPreExcuteHooks.splice(0,formPreExcuteHooks.length)
  formValidateHooks.splice(0,formValidateHooks.length)
  formResetHooks.splice(0,formResetHooks.length)
})

const getHeight = () => {
  return refContainer.value?.clientHeight??0
}

defineExpose({
  submit,
  reset,
  getHeight
})
</script>

<template>
  <div ref="refContainer" class="flex flex-col gap-3">
    <slot></slot>
  </div>
</template>

<style>

</style>
